"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const common_1 = require("../common");
const CENTIMETERS_PER_INCH = 2.54;
const POINTS_PER_INCH = 72;
const CENTIMETERS_PER_POINT = CENTIMETERS_PER_INCH / POINTS_PER_INCH;
function getObj(filenameWithoutExtension, triangleMesh, positions, viewBox, scale, uvCenter) {
    const { vertices, faces, facesForwardMapping } = triangleMesh;
    // First check that geometry is valid
    if (vertices.length === 0) {
        throw new Error('Unable to create OBJ, no vertices in triangle mesh.');
    }
    if (faces.length === 0) {
        throw new Error('Unable to create OBJ, no faces in triangle mesh.');
    }
    // Transform the 3D positions to match Adobe Dimension's coordinate system:
    // - Translate so that the object center in X and Y and its minimum in Z lies at the origin.
    // - Scale from the unit cube to the actual size of the dieline, and from units of points
    //   (72 points per inch) to Dimension's units of centimeters.
    // - Rotate around X by -90 degrees to go from our Z-up coordinate system to Dimension's
    //   Y-up coordinate system.
    const bounds = common_1.geom.getBounds(positions);
    const origin = [
        (bounds.min[0] + bounds.max[0]) / 2,
        (bounds.min[1] + bounds.max[1]) / 2,
        bounds.min[2],
    ];
    const scale3D = scale * CENTIMETERS_PER_POINT;
    const transformedPositions = positions.map((position) => {
        const p = common_1.vec.multiply(common_1.vec.subtract(position, origin), scale3D);
        return [p[0], p[2], -p[1]];
    });
    // Define functions that round numbers and vectors to six decimal places.
    const roundNumber = (x) => Math.round(x * 1e6) / 1e6;
    const roundVector = (v) => v.map(roundNumber);
    const forEachTriangle = (triangleAction) => {
        let i = 0;
        // Iterate over each dieline panel.
        facesForwardMapping.forEach((faceForwardMapping) => {
            // Iterate over each triangle within the current panel.
            faceForwardMapping.forEach((faceIndex) => {
                // Pass the vertex indices to the specified function.
                const face = faces[faceIndex];
                const v1 = face.vertices[0];
                const v2 = face.vertices[2];
                const v3 = face.vertices[1];
                triangleAction(v1, v2, v3, i);
                i += 1;
            });
        });
    };
    // Start the OBJ file by referring to a material library, naming the object,
    // and using a particular material.
    let output = `mtllib ${filenameWithoutExtension}.mtl\n`;
    output += 'o Model\n';
    output += 'usemtl Textured Material 1\n';
    // Output the positions of all the vertices. These are shared across dieline panels.
    output += '\n# Vertices\n';
    transformedPositions.forEach((position) => {
        const p = roundVector(position);
        output += `v ${p[0]} ${p[1]} ${p[2]}\n`;
    });
    // Output the texture coordinates of all the vertices. These are also shared.
    // Note that we flip the v coordinate, since Dimension and ThreeJS use opposite conventions.
    // We also need to offset the uvs using the original bounds of dieline artboard/viewBox.
    output += '\n# Texture coordinates\n';
    vertices.forEach((vertex) => {
        const uv = common_1.vec.add(common_1.vec.multiply(vertex.position2D, scale), uvCenter);
        const u = roundNumber((uv[0] - viewBox.min[0]) / (viewBox.max[0] - viewBox.min[0]));
        const v = roundNumber(1 - ((uv[1] - viewBox.min[1]) / (viewBox.max[1] - viewBox.min[1])));
        output += `vt ${u} ${v}\n`;
    });
    // Output one normal per triangle.
    // TODO: Offer smooth shading as an option, where normals at each vertex are computed by
    // averaging over adjacent triangles withint the same dieline panel.
    output += '\n# Normals\n';
    forEachTriangle((v1, v2, v3) => {
        const e1 = common_1.vec.subtract(transformedPositions[v2], transformedPositions[v1]);
        const e2 = common_1.vec.subtract(transformedPositions[v3], transformedPositions[v1]);
        const normal = roundVector(common_1.vec.normalize(common_1.vec.cross(e1, e2)));
        output += `vn ${normal[0]} ${normal[1]} ${normal[2]}\n`;
    });
    // Output flat-shaded faces.
    output += '\n# Faces\n';
    forEachTriangle((v1, v2, v3, i) => {
        // Output the vertex, texture, and normal indices for the current triangle.
        const w1 = v1 + 1;
        const w2 = v2 + 1;
        const w3 = v3 + 1;
        const j = i + 1;
        output += `f ${w1}/${w1}/${j} ${w2}/${w2}/${j} ${w3}/${w3}/${j}\n`;
    });
    return output;
}
function getMtl(filenameWithoutExtension) {
    return 'newmtl Textured Material 1\n'
        + 'illum 4\n'
        + 'Kd 0 0 0\n'
        + 'Ka 0 0 0\n'
        + 'Tf 1 1 1\n'
        + `map_Kd ${filenameWithoutExtension}.png\n`
        + 'Ni 1\n'
        + 'Ks 0.5 0.5 0.5\n'
        + 'Ns 18\n';
}
/**
 * Converts the given triangle mesh into OBJ and MTL format.
 * @param filenameWithoutExtension The filename (without extension) to use for the OBJ and MTL
 * files.
 * @param triangleMesh A triangle mesh.
 * @param positions The 3D positions of the vertices.
 * @param viewBox The 2D bounds of the original dieline's artboard.
 * @param scale The amount to scale the 3D mesh.
 * @param uvCenter The center of the UV coordinate system.
 */
function triangleMeshToObj(filenameWithoutExtension, triangleMesh, positions, viewBox, scale, uvCenter) {
    const obj = getObj(filenameWithoutExtension, triangleMesh, positions, viewBox, scale, uvCenter);
    const mtl = getMtl(filenameWithoutExtension);
    return { obj, mtl };
}
exports.triangleMeshToObj = triangleMeshToObj;
//# sourceMappingURL=triangleMeshToObj.js.map